import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Box, Container, Heading, Text, VStack, HStack, SimpleGrid, Image, Stack } from '@chakra-ui/react';
import { ServiceSection } from '../components/ServiceSection';
import { CTAButton } from '../components/CTAButton';
import { StickyTopBar } from '../components/StickyTopBar';
import { FloatingWhatsAppButton } from '../components/FloatingWhatsAppButton';
import { ScrollReveal } from '../components/ScrollReveal';
import { FireParticles } from '../components/FireParticles';
import { SectionFade } from '../components/SectionFade';
import { LazyVideo } from '../components/LazyVideo';
const TestimonialsSection = lazy(() => import('../components/TestimonialsSection').then(m => ({
  default: m.TestimonialsSection
})));
const VideoCallSection = lazy(() => import('../components/VideoCallSection').then(m => ({
  default: m.VideoCallSection
})));
const HERO_VIDEO = 'https://cdn.magicpatterns.com/uploads/8MtiGQzPf5Uwfq8gXqGonv/pikaso-untitled-project-2026-02-24%20(1).mp4';
export function LandingPage() {
  const whatsappUrl = 'https://wa.me/+16058247498';
  const phoneUrl = 'tel:+16055406973';
  const videoCallUrl = `${whatsappUrl}?text=${encodeURIComponent('Quiero agendar una consulta por videollamada')}`;
  const [videoFailed, setVideoFailed] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let loaded = false;
    const loadChat = () => {
      if (loaded) return;
      loaded = true;
      cleanup();
      const s1 = document.createElement('script');
      s1.async = true;
      s1.src = 'https://embed.tawk.to/6839ef8f9796bf190d6448da/1ish4vion';
      s1.charset = 'UTF-8';
      s1.setAttribute('crossorigin', '*');
      document.head.appendChild(s1);
    };

    // Load the chat widget on the first real interaction, or once the main
    // thread is idle — never while the hero is still painting.
    const events: Array<keyof WindowEventMap> = ['scroll', 'pointerdown', 'keydown'];
    const cleanup = () => {
      events.forEach(event => window.removeEventListener(event, loadChat));
      clearTimeout(fallback);
    };
    events.forEach(event => window.addEventListener(event, loadChat, {
      once: true,
      passive: true
    }));
    const fallback = window.setTimeout(loadChat, 12000);
    return cleanup;
  }, []);
  return <Box bg="#0a0a0a" minH="100vh" w="full" overflowX="hidden" data-id="element-84" data-mp="3o3wskr376c1u">
      <StickyTopBar whatsappUrl={whatsappUrl} data-id="element-85" data-mp="3j63kttm8x9zg" />
      <FloatingWhatsAppButton whatsappUrl={whatsappUrl} data-id="element-86" data-mp="3j5c0xg20h0p0" />

      {/* ---------------- Hero ---------------- */}
      <Box as="section" position="relative" pt={{
      base: 24,
      md: 32
    }} pb={{
      base: 12,
      md: 16
    }} overflow="hidden" data-id="element-87" data-mp="r2rcsp3e5vt">
        <Box position="absolute" inset={0} zIndex={0} data-id="element-88" data-mp="anpa9bxuymv7">
          <Image src="https://cdn.magicpatterns.com/uploads/vcEBSogWdf7QtPCTn7govW/freepik__the-style-is-candid-image-photography-with-natural__67051.png" alt="" w="full" h="full" htmlWidth="1600" htmlHeight="1000" objectFit="cover" filter="brightness(0.3)" fetchpriority="high" decoding="async" data-id="element-89" data-mp="5s9z4rr8j52h" />
          <Box position="absolute" inset={0} bgGradient="linear(to-b, rgba(184, 41, 26, 0.32), rgba(10, 10, 10, 0.92) 70%, #0a0a0a)" data-id="element-90" data-mp="kfqve7qtlm0q" />
        </Box>

        <FireParticles count={20} data-id="element-91" data-mp="beamy5jkftz5" />

        <Container maxW="container.xl" position="relative" zIndex={2} data-id="element-92" data-mp="3stppt4y58zc4">
          <VStack spacing={5} textAlign="center" data-id="element-93" data-mp="3srmp9qzd7nt5">
            <ScrollReveal data-id="element-94" data-mp="1o7ty5cg2g0t">
              <VStack spacing={5} data-id="element-95" data-mp="1nfwsq378a8f">
                <Heading as="h1" fontSize={{
                base: '3xl',
                md: '5xl',
                lg: '6xl'
              }} fontWeight="black" textTransform="uppercase" letterSpacing="tight" color="white" lineHeight="1.1" maxW="920px" textShadow="0 0 26px rgba(232, 99, 26, 0.5), 0 0 60px rgba(184, 41, 26, 0.35)" data-id="element-96" data-mp="3jsrm6nrmatu2">
                  Descubre si hay energía bloqueando tu camino
                </Heading>

                <Text fontSize={{
                base: 'md',
                md: 'xl'
              }} color="gray.300" maxW="760px" lineHeight="tall" data-id="element-97" data-mp="2y0ymhrr9tbru">
                  A veces lo que sientes como distancia o cambios en una
                  relación tiene una explicación energética. Te ayudamos a
                  identificarlo.
                </Text>

                <Text fontSize={{
                base: 'md',
                md: 'lg'
              }} color="gold.400" fontWeight="semibold" maxW="640px" data-id="element-98" data-mp="m8ry4ymzcwif">
                  Una consulta personalizada para entender qué está pasando en
                  tu energía.
                </Text>
              </VStack>
            </ScrollReveal>

            {!videoFailed && <VStack spacing={2} w="full" data-id="element-99" data-mp="13jylg6dvq19w">
                <Box position="relative" w="full" maxW="780px" mx="auto" mt={4} borderRadius="xl" overflow="hidden" borderWidth="1px" borderColor="rgba(232, 99, 26, 0.45)" boxShadow="0 0 46px rgba(184, 41, 26, 0.35)" data-id="element-100" data-mp="6uh58xh9swty">
                  <LazyVideo src={HERO_VIDEO} aspectRatio="16 / 9" label="Reproducir el video de presentación" onError={() => setVideoFailed(true)} data-id="element-101" data-mp="1rakjd4u66566" />
                </Box>

                <Text fontSize="xs" color="gray.400" fontStyle="italic" maxW="760px" data-id="element-102" data-mp="16mt7zeaihel5">
                  Martha Molina comparte su experiencia con nuestro trabajo
                  espiritual.
                </Text>
              </VStack>}
          </VStack>
        </Container>
      </Box>

      {/* ---------------- Contact CTA ---------------- */}
      <Box as="section" position="relative" pb={{
      base: 14,
      md: 20
    }} data-id="element-103" data-mp="iwgwxujyz1qc">
        <Container maxW="container.md" data-id="element-104" data-mp="xkts4pb8ggjs">
          {/* Not wrapped in a reveal: the primary CTAs must be tappable instantly. */}
          <Box data-id="element-105" data-mp="sstlke0nqpe8">
            <VStack spacing={5} textAlign="center" borderTopWidth="1px" borderColor="rgba(232, 99, 26, 0.35)" pt={{
            base: 10,
            md: 12
          }} data-id="element-106" data-mp="ss0l1usakw4j">
              <Heading as="h2" fontSize={{
              base: '2xl',
              md: '4xl'
            }} color="gold.400" fontWeight="black" lineHeight="1.2" textShadow="0 0 24px rgba(242, 169, 59, 0.45)" data-id="element-107" data-mp="33hkxsqp3vu">
                Agenda tu consulta espiritual
              </Heading>

              <Stack direction={{
              base: 'column',
              md: 'row'
            }} spacing={4} w="full" maxW="620px" justify="center" data-id="element-108" data-mp="26qu0io98ex3n">
                <CTAButton href={whatsappUrl} flex={1} data-id="element-109" data-mp="2lhjfm1ptcwcs">
                  Escríbenos por WhatsApp
                </CTAButton>
                <CTAButton href={phoneUrl} tone="call" flex={1} data-id="element-110" data-mp="2plr4byw9ucb0">
                  Llamar Ahora
                </CTAButton>
              </Stack>

              <Text fontSize="sm" color="gray.400" data-id="element-111" data-mp="1e4rerb1b6bko">
                Atención privada y personalizada.
              </Text>
            </VStack>
          </Box>
        </Container>
      </Box>

      <SectionFade data-id="element-112" data-mp="20shlomwkwosu" />

      {/* ---------------- How we work ---------------- */}
      <Box as="section" className="defer-paint" position="relative" py={{
      base: 14,
      md: 20
    }} overflow="hidden" data-id="element-113" data-mp="1wre3htr26gog">
        <Box position="absolute" inset={0} zIndex={0} data-id="element-114" data-mp="2oikbsxgxnkot">
          <Image src="https://images.unsplash.com/photo-1604881991720-f91add269bed?w=900&q=60&fm=webp&auto=format&fit=crop" alt="" w="full" h="full" htmlWidth="900" htmlHeight="600" objectFit="cover" filter="brightness(0.22)" loading="lazy" decoding="async" data-id="element-115" data-mp="2ohack4ycg6gl" />
          <Box position="absolute" inset={0} bg="rgba(10, 10, 10, 0.72)" data-id="element-116" data-mp="387mac7diwe5x" />
        </Box>

        <Container maxW="container.lg" position="relative" zIndex={1} data-id="element-117" data-mp="33fkw4fwsodd9">
          <ScrollReveal data-id="element-118" data-mp="3ar8x50kb9k9i">
            <VStack spacing={6} textAlign="center" data-id="element-119" data-mp="3arsdicubc0lf">
              <Heading as="h2" fontSize={{
              base: '2xl',
              md: '4xl'
            }} color="fire.400" textTransform="uppercase" fontWeight="black" letterSpacing="wide" lineHeight="1.2" textShadow="0 0 26px rgba(232, 99, 26, 0.5)" data-id="element-120" data-mp="3atbwwcb9mrrf">
                Trabajamos con intención y experiencia real
              </Heading>

              <Text fontSize={{
              base: 'md',
              md: 'lg'
            }} color="gray.300" maxW="780px" lineHeight="tall" data-id="element-121" data-mp="2hh7k0dkujhw">
                Cada consulta se analiza de forma individual. Contamos con más
                de 40 años de práctica en tradición ancestral. Cada ritual se
                prepara según la situación de cada persona.
              </Text>

              <Box bg="rgba(184, 41, 26, 0.1)" borderWidth="1px" borderColor="rgba(232, 99, 26, 0.4)" borderRadius="lg" px={{
              base: 6,
              md: 10
            }} py={6} mt={2} maxW="700px" data-id="element-122" data-mp="1g8rrcus0ixt0">
                <Text fontSize={{
                base: 'md',
                md: 'lg'
              }} color="gold.400" fontWeight="semibold" data-id="element-123" data-mp="1uy7gk6bwav6l">
                  Escuchamos tu caso con respeto y te explicamos con claridad
                  cómo podemos acompañarte.
                </Text>
              </Box>

              <CTAButton href={whatsappUrl} mt={4} maxW="420px" w="full" data-id="element-124" data-mp="2du76zcp85qo2">
                Consulta tu caso
              </CTAButton>
            </VStack>
          </ScrollReveal>
        </Container>
      </Box>

      <SectionFade flip data-id="element-125" data-mp="jj207ziucy4i" />

      {/* ---------------- Services ---------------- */}
      <Box as="section" className="defer-paint" py={{
      base: 12,
      md: 16
    }} data-id="element-126" data-mp="2wsihula43hts">
        <Container maxW="container.xl" data-id="element-127" data-mp="2rzfw2zbktco5">
          <ScrollReveal data-id="element-128" data-mp="2ryo1nrtptkzg">
            <VStack spacing={3} textAlign="center" mb={{
            base: 10,
            md: 14
          }} data-id="element-129" data-mp="2nynlo2qyx1qa">
              <Heading as="h2" fontSize={{
              base: '2xl',
              md: '4xl'
            }} color="white" fontWeight="black" lineHeight="1.2" data-id="element-130" data-mp="2nwjdv9py7ikq">
                Nuestros servicios
              </Heading>
              <Text fontSize={{
              base: 'sm',
              md: 'md'
            }} color="gray.400" maxW="640px" data-id="element-131" data-mp="36ovkw9rjr817">
                Elige el acompañamiento que mejor se ajuste a lo que estás
                viviendo.
              </Text>
            </VStack>
          </ScrollReveal>

          <SimpleGrid columns={{
          base: 1,
          md: 2,
          lg: 3
        }} spacing={8} data-id="element-132" data-mp="1v85b6gnwzy2u">
            <ScrollReveal h="full" data-id="element-133" data-mp="1rtdmcotlegpm">
              <ServiceSection imageUrl="https://cdn.magicpatterns.com/uploads/wmoq8U6ujmtp9hKFZSLm6E/freepik__create-a-logo-featuring-two-real-people-an-african__48719.jpg" title="Fortalecer la conexión de pareja" description="Un acompañamiento para trabajar el vínculo afectivo, la distancia emocional y la comunicación dentro de la relación." features={['Orientación en vínculos afectivos', 'Sanación de la conexión emocional', 'Acompañamiento en procesos de reconciliación', 'Apertura a la comunicación']} ctaText="Conoce este servicio" ctaHref={whatsappUrl} variant="highlighted" data-id="element-134" data-mp="1rq7u50po2ypo" />
            </ScrollReveal>

            <ScrollReveal delay={120} h="full" data-id="element-135" data-mp="v1s5r7yamve5">
              <ServiceSection imageUrl="https://cdn.magicpatterns.com/uploads/dk9mQGgEQPHabcM6NvRidH/Captura_de_pantalla_2025-02-26_103828_(1).png" title="Limpieza y protección espiritual" description="Un trabajo enfocado en tu propia energía: despejar cargas, recuperar equilibrio y fortalecer tu protección personal." features={['Limpieza energética', 'Protección espiritual', 'Armonización personal', 'Orientación en situaciones de tensión']} ctaText="Conoce este servicio" ctaHref={whatsappUrl} data-id="element-136" data-mp="v1idfdps0c5a" />
            </ScrollReveal>

            <ScrollReveal delay={240} h="full" data-id="element-137" data-mp="146rtw95x4iid">
              <ServiceSection imageUrl="https://cdn.magicpatterns.com/uploads/fZSvWUfPPMxEtiAyE4NKRd/Captura_de_pantalla_2025-05-30_225505_(1).png" title="Apertura de caminos y abundancia" description="Cuando sientes que todo se estanca, trabajamos la energía alrededor de tus proyectos y tu estabilidad." features={['Rituales de apertura de caminos', 'Activación de prosperidad', 'Movimiento de energías estancadas', 'Atracción de estabilidad']} ctaText="Conoce este servicio" ctaHref={whatsappUrl} data-id="element-138" data-mp="z6vnk39r0ezn" />
            </ScrollReveal>
          </SimpleGrid>
        </Container>
      </Box>

      <SectionFade data-id="element-139" data-mp="2omg5410m1r1b" />

      {/* ---------------- Testimonials ---------------- */}
      <Suspense fallback={null} data-id="element-140" data-mp="2tmav82ckaclb">
        <TestimonialsSection data-id="element-141" data-mp="2tkzqo0bal0t5" />
      </Suspense>

      {/* ---------------- Video call ---------------- */}
      <Suspense fallback={null} data-id="element-142" data-mp="2xofnoxr9x36d">
        <VideoCallSection scheduleUrl={videoCallUrl} data-id="element-143" data-mp="2xljrbdv2qmk1" />
      </Suspense>

      <SectionFade flip data-id="element-144" data-mp="8vaejv1z3t45" />

      {/* ---------------- Closing ---------------- */}
      <Box as="section" className="defer-paint" position="relative" py={{
      base: 16,
      md: 24
    }} overflow="hidden" data-id="element-145" data-mp="cwdusinxnr5w">
        <Box position="absolute" inset={0} zIndex={0} data-id="element-146" data-mp="1tdmb7kgayn0v">
          <Image src="https://images.unsplash.com/photo-1501139083538-0139583c060f?w=900&q=60&fm=webp&auto=format&fit=crop" alt="" w="full" h="full" htmlWidth="900" htmlHeight="600" objectFit="cover" filter="brightness(0.24)" loading="lazy" decoding="async" data-id="element-147" data-mp="1tg8hlgrgiwhi" />
          <Box position="absolute" inset={0} bgGradient="linear(to-b, rgba(10,10,10,0.8), rgba(184, 41, 26, 0.22))" data-id="element-148" data-mp="19rzrrdddz2l6" />
        </Box>

        <FireParticles count={14} opacity={0.75} data-id="element-149" data-mp="10qrrwqv81toz" />

        <Container maxW="container.lg" position="relative" zIndex={2} data-id="element-150" data-mp="30219bdfu0mqu">
          <ScrollReveal data-id="element-151" data-mp="3008agkbgn6te">
            <VStack spacing={6} textAlign="center" data-id="element-152" data-mp="2v3hoxhgvzj0l">
              <Heading as="h2" fontSize={{
              base: '3xl',
              md: '5xl'
            }} color="white" fontWeight="black" letterSpacing="tight" lineHeight="1.15" textShadow="0 0 28px rgba(232, 99, 26, 0.45)" data-id="element-153" data-mp="2v3zrswy20vhh">
                Estamos aquí para acompañarte
              </Heading>

              <Text fontSize={{
              base: 'md',
              md: 'lg'
            }} color="gray.300" maxW="680px" lineHeight="tall" data-id="element-154" data-mp="1qpldmjzps7k">
                Si tienes dudas sobre lo que estás viviendo, puedes escribirnos
                y conversar tu caso con calma. Te explicamos cómo funciona la
                consulta y resolvemos tus preguntas antes de comenzar.
              </Text>

              <Box bg="rgba(10, 10, 10, 0.7)" borderWidth="1px" borderColor="rgba(242, 169, 59, 0.4)" borderRadius="xl" p={{
              base: 6,
              md: 10
            }} mt={4} w="full" maxW="640px" backdropFilter="blur(6px)" data-id="element-155" data-mp="2hhkiz22djxi9">
                <VStack spacing={4} data-id="element-156" data-mp="2dzwikj3a7sy4">
                  <CTAButton href={whatsappUrl} w="full" data-id="element-157" data-mp="2e0y45hhxbxu3">
                    WhatsApp Directo
                  </CTAButton>
                  <CTAButton href={phoneUrl} tone="call" w="full" data-id="element-158" data-mp="1q7zi1o5a8ncb">
                    Llamada Privada
                  </CTAButton>
                  <CTAButton href={videoCallUrl} tone="whatsapp" w="full" data-id="element-159" data-mp="1zzlqxdu5eabw">
                    Quiero Agendar mi Consulta
                  </CTAButton>
                </VStack>
              </Box>
            </VStack>
          </ScrollReveal>
        </Container>
      </Box>

      {/* ---------------- Footer ---------------- */}
      <Box as="footer" bg="#080808" py={10} borderTop="1px solid" borderColor="gray.900" data-id="element-160" data-mp="12jg9r8m8gak8">
        <Container maxW="container.xl" data-id="element-161" data-mp="i0h9pch8rnqc">
          <VStack spacing={3} textAlign="center" data-id="element-162" data-mp="lzpzk695v1gm">
            <HStack spacing={2} color="gold.500" fontSize="sm" data-id="element-163" data-mp="m0jbrmqw2j65">
              <Text data-id="element-164" data-mp="qz2ulea6reua">Tradición ancestral</Text>
              <Text color="gray.700" data-id="element-165" data-mp="r1fe3vvldx9g">|</Text>
              <Text data-id="element-166" data-mp="vypajvhef96s">Más de 40 años de práctica</Text>
            </HStack>
            <Text color="gray.500" fontSize="xs" maxW="720px" lineHeight="tall" data-id="element-167" data-mp="10xid4sv5dfs6">
              Nuestros servicios son de carácter espiritual y de
              acompañamiento personal. No sustituyen atención médica,
              psicológica, legal ni financiera. Cada proceso es individual y las
              experiencias pueden variar de una persona a otra.
            </Text>
          </VStack>
        </Container>
      </Box>
    </Box>;
}